import { AnnouncementMarkdown } from "@/components/AnnouncementMarkdown";
import { Button } from "@/components/flexkit/Button";
import { IconCrossLarge } from "central-icons/IconCrossLarge";
import { Link } from "@/components/flexkit/Link";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import type { ChangelogData } from "@/hooks/use-changelog";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useCallback, useEffect, useRef, useState, type FC } from "react";

const thumbnailClass = "mt-3 max-h-40 w-full rounded-md border border-border-base object-cover";

/**
 * Renders a changelog thumbnail. SVG URLs are fetched and injected inline
 * so SMIL animations play. Raster images use a standard <img>. Handles
 * missing URLs and fetch/load errors gracefully.
 */
const WidgetThumbnail: FC<{ src: string | undefined; alt: string }> = ({ src, alt }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [failed, setFailed] = useState(false);
    const isSvg = src?.endsWith(".svg");

    useEffect(() => {
        if (!src || !isSvg || !containerRef.current) return;

        let cancelled = false;
        fetch(src)
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.text();
            })
            .then((svgText) => {
                if (!cancelled && containerRef.current) {
                    containerRef.current.innerHTML = svgText;
                    // Apply styling to the injected <svg> element
                    const svgEl = containerRef.current.querySelector("svg");
                    if (svgEl) {
                        svgEl.classList.add("w-full", "max-h-40", "rounded-md");
                        svgEl.style.display = "block";
                    }
                }
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });

        return () => {
            cancelled = true;
        };
    }, [src, isSvg]);

    if (!src || failed) return null;

    if (isSvg) {
        return <div ref={containerRef} className={thumbnailClass} />;
    }

    return <img src={src} alt={alt} className={thumbnailClass} loading="lazy" onError={() => setFailed(true)} />;
};

/** How far the popover extends past the sidebar's right edge (expanded mode). */
const SIDEBAR_OVERSHOOT = 40;
/** Fixed width when the sidebar is collapsed. */
const COLLAPSED_WIDTH = 320;

type ChangelogPreviewProps = {
    changelog: ChangelogData;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onLearnMore: () => void;
    /** Popover placement relative to the trigger. Defaults to "top". */
    side?: "top" | "right" | "bottom" | "left";
    /** Render the popover anchored to this trigger element. */
    children: React.ReactNode;
};

/**
 * Small popover card showing a preview of the latest changelog entry.
 * Uses Radix Popover for click-outside and Escape key dismissal.
 * "Learn more" opens the full changelog dialog.
 * "Try it" only appears when the changelog has an actionUrl.
 */
export const ChangelogPreview: FC<ChangelogPreviewProps> = ({
    changelog,
    open,
    onOpenChange,
    onLearnMore,
    side = "top",
    children,
}) => {
    const triggerRef = useRef<HTMLElement | null>(null);
    const [popoverWidth, setPopoverWidth] = useState<number>(COLLAPSED_WIDTH);
    const widgetImageSrc = changelog.widgetImageUrl || changelog.imageUrl;

    const measureSidebar = useCallback((node: HTMLElement | null) => {
        triggerRef.current = node;
        if (node) {
            const sidebar = node.closest<HTMLElement>('[data-testid="sidebar"]');
            if (sidebar) {
                setPopoverWidth(sidebar.offsetWidth + SIDEBAR_OVERSHOOT);
            }
        }
    }, []);

    // Collapsed sidebar: use fixed width. Expanded: sidebar width + overshoot.
    const width = side === "right" ? COLLAPSED_WIDTH : popoverWidth;

    return (
        <PopoverPrimitive.Root
            open={open}
            onOpenChange={(nextOpen) => {
                if (nextOpen && triggerRef.current) {
                    const sidebar = triggerRef.current.closest<HTMLElement>('[data-testid="sidebar"]');
                    if (sidebar) {
                        setPopoverWidth(sidebar.offsetWidth + SIDEBAR_OVERSHOOT);
                    }
                }
                onOpenChange(nextOpen);
            }}
        >
            <PopoverPrimitive.Trigger asChild ref={measureSidebar}>
                {children}
            </PopoverPrimitive.Trigger>
            <PopoverPrimitive.Portal>
                <PopoverPrimitive.Content
                    side={side}
                    sideOffset={8}
                    align={side === "right" ? "center" : "end"}
                    alignOffset={side === "right" ? 0 : SIDEBAR_OVERSHOOT}
                    collisionPadding={16}
                    style={{ width }}
                    className={cn(
                        "z-50 flex select-none flex-col rounded-lg border border-border-base bg-surface-primary shadow-md",
                        "animate-in fade-in-0 zoom-in-95",
                        side === "right" ? "slide-in-from-left-2" : "slide-in-from-bottom-2",
                    )}
                    data-testid="changelog-preview"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
                        <Text className="text-sm font-semibold text-content-primary">What&apos;s new</Text>
                        <PopoverPrimitive.Close asChild>
                            <Button
                                variant="ghost"
                                size="xs"
                                className="-mr-1 p-1"
                                aria-label="Dismiss"
                                data-tracking-id="dismiss-changelog-preview"
                            >
                                <IconCrossLarge size={10} />
                            </Button>
                        </PopoverPrimitive.Close>
                    </div>

                    {/* Content */}
                    <div className="flex flex-col px-5 pb-4 pt-3">
                        {/* Title */}
                        <Text className="text-md font-semibold text-content-primary">{changelog.title}</Text>

                        {/* Date */}
                        {changelog.date && (
                            <Text className="mt-0.5 text-sm text-content-quaternary">{changelog.date}</Text>
                        )}

                        {/* Hero image — prefer widget-specific thumbnail when available */}
                        <WidgetThumbnail src={widgetImageSrc} alt={changelog.title} />

                        {/* Description */}
                        <div className="mt-3 line-clamp-3 text-base text-content-secondary">
                            <AnnouncementMarkdown content={changelog.description} />
                        </div>

                        {/* Actions */}
                        <div className="mt-4 flex flex-col gap-2">
                            <Button
                                variant="secondary"
                                size="md"
                                className="w-full"
                                onClick={onLearnMore}
                                data-tracking-id="changelog-learn-more"
                            >
                                Learn more
                            </Button>
                            {changelog.actionUrl && (
                                <Button
                                    variant="primary"
                                    size="md"
                                    className="w-full"
                                    asChild
                                    data-tracking-id="changelog-try-now"
                                >
                                    <Link to={changelog.actionUrl}>Try it</Link>
                                </Button>
                            )}
                        </div>
                    </div>
                </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
    );
};
